import type { TitrationDecisionReason } from "@/constants/titration";

import { fhirR4 } from "@smile-cdr/fhirts";
import { gql } from "@urql/core";

import { CODEABLE_CONCEPT_FRAGMENT, DOSAGE_FRAGMENT, RATIO_FRAGMENT } from "./fhir_fragment";

// ============================================
// Fragments
// ============================================

const TITRATION_CARE_PLAN_FRAGMENT = gql`
  fragment TitrationCarePlanFields on CarePlanModel {
    id
    status
    intent
    period {
      start
      end
    }
  }
`;

const TITRATION_MEDICATION_REQUEST_FRAGMENT = gql`
  fragment TitrationMedicationRequestFields on MedicationRequestModel {
    id
    status
    intent
    category {
      ...CodeableConceptFields
    }
    contained {
      resourceType
      id
      form {
        text
      }
      code {
        ...CodeableConceptFields
      }
      ingredient {
        itemCodeableConcept {
          ...CodeableConceptFields
        }
        isActive
        strength {
          ...RatioFields
        }
      }
    }
    medicationReference {
      display
      reference
    }
    dispenseRequest {
      validityPeriod {
        start
        end
      }
    }
    dosageInstruction {
      ...DosageFields
      extension {
        url
        valueDosage {
          ...DosageFields
        }
      }
    }
    extension {
      url
      valueCodeableConcept {
        coding {
          code
          display
          system
        }
        text
      }
    }
  }
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${DOSAGE_FRAGMENT}
  ${RATIO_FRAGMENT}
`;

const TITRATION_MEDICATION_STATEMENT_FRAGMENT = gql`
  fragment TitrationMedicationStatementFields on MedicationStatementModel {
    id
    resourceType
    category {
      ...CodeableConceptFields
    }
    dosage {
      ...DosageFields
    }
    effectiveDateTime
    effectivePeriod {
      start
      end
    }
    contained {
      resourceType
      id
      code {
        ...CodeableConceptFields
      }
      ingredient {
        itemCodeableConcept {
          ...CodeableConceptFields
        }
        isActive
        strength {
          ...RatioFields
        }
      }
    }
    medicationReference {
      display
      reference
    }
    status
    subject {
      reference
    }
    basedOn {
      reference
      type
    }
  }
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${DOSAGE_FRAGMENT}
`;
const TITRATION_CARE_PLAN_ENTRY_FRAGMENT = gql`
  fragment TitrationCarePlanResult on TitrationCarePlanEntry {
    carePlan {
      ...TitrationCarePlanFields
    }
    medicationRequests {
      ...TitrationMedicationRequestFields
    }
    medicationStatements {
      ...TitrationMedicationStatementFields
    }
  }
  ${TITRATION_CARE_PLAN_FRAGMENT}
  ${TITRATION_MEDICATION_REQUEST_FRAGMENT}
  ${TITRATION_MEDICATION_STATEMENT_FRAGMENT}
`;

// ============================================
// Titration Queries/Mutations
// ============================================

export const GET_TITRATION_CARE_PLAN_QUERY = gql`
  query getTitrationCarePlan($input: GetTitrationCarePlanInput!) {
    getTitrationCarePlan(input: $input) {
      ...TitrationCarePlanResult
    }
  }
  ${TITRATION_CARE_PLAN_ENTRY_FRAGMENT}
`;

/*
  Titration CarePlan (VMD-608)
  Draft creation flow for GDMT titration plans. Mutation calls into the
  Engage TitrationService, which composes a single FHIR transaction bundle
  (CarePlan + N MedicationRequests) and returns the resolved entry.
*/
export const CREATE_TITRATION_CARE_PLAN_MUTATION = gql`
  mutation createTitrationCarePlan($input: CreateTitrationCarePlanInput!) {
    createTitrationCarePlan(input: $input) {
      ...TitrationCarePlanResult
    }
  }
  ${TITRATION_CARE_PLAN_ENTRY_FRAGMENT}
`;

export const END_TITRATION_CARE_PLAN_MUTATION = gql`
  mutation endTitrationCarePlan($input: EndTitrationCarePlanInput!) {
    endTitrationCarePlan(input: $input)
  }
`;

// ===================================================
// TypeScript Types: TitrationCarePlan
// ===================================================

export interface TitrationMedicationRequest {
  id: string;
  status: string;
  intent: string;
  category?: fhirR4.CodeableConcept[] | null;
  medicationCodeableConcept?: { text?: string } | null;
  extension: fhirR4.Extension[];
  dosageInstruction: fhirR4.Dosage[];
  dispenseRequest?: { validityPeriod?: { start: string | null; end: string | null } | null } | null;
}

export interface TitrationCarePlan {
  carePlan: fhirR4.CarePlan;
  medicationRequests: fhirR4.MedicationRequest[];
  medicationStatements: fhirR4.MedicationStatement[];
}

export interface CreateTitrationCarePlanData {
  createTitrationCarePlan: TitrationCarePlan;
}

// ===================================================
// TypeScript Types: getTitrationCarePlan
// ===================================================

export interface GetTitrationCarePlanInput {
  care_plan_id?: string;
  patient_id?: string;
}

export interface GetTitrationCarePlanResponse {
  getTitrationCarePlan: TitrationCarePlan | null;
}

// ===================================================
// TypeScript Types: createTitrationCarePlan
// ===================================================

export interface CodingInput {
  system?: string;
  code?: string;
  display?: string;
}

export interface CodeableConceptInput {
  coding?: CodingInput[];
  text?: string;
}

export interface QuantityInput {
  value?: number;
  unit?: string;
  system?: string;
  code?: string;
}

export interface TimingRepeatInput {
  frequency?: number;
  period?: number;
  periodUnit?: string;
  when?: string[];
}

export interface TimingInput {
  repeat?: TimingRepeatInput;
}

export interface DosageInput {
  sequence?: number;
  text?: string;
  patientInstruction?: string;
  asNeededBoolean?: boolean;
  doseAndRate?: DoseAndRateInput[];
  timing?: TimingInput;
  route?: CodeableConceptInput;
}

export interface DoseAndRateInput {
  doseQuantity?: QuantityInput;
}

export interface RatioInput {
  numerator?: QuantityInput;
  denominator?: QuantityInput;
}

/** Medication.ingredient[] — the strength, e.g. "25 mg per 1 tablet". */
export interface MedicationIngredientInput {
  itemCodeableConcept?: CodeableConceptInput;
  isActive?: boolean;
  strength?: RatioInput;
}

export interface TitrationStepInput {
  // Contains medication strength (25 mg)
  ingredient: MedicationIngredientInput[];
  // Contains medication form (ex. capsule, drop, puff, etc.)
  form: CodeableConceptInput;
  // Contains medication frequency and quantity
  dosage?: DosageInput[];
}

export interface BaseTitrationMedicationInput {
  drug_class: TitrationDrugClass;
  date_started?: string;
  medication_codeable_concept: CodeableConceptInput;
  current: TitrationStepInput;
  target: TitrationStepInput;
  reason_for_titration?: string;
  source_medication_statement_id?: string;
  decision?: TitrationDecision;
  decision_reasons?: TitrationDecisionReason[];
  // The medication's specific class. For RASi the BE requires one of acei / arbs
  // / arnis (the sub-category the practitioner picks); for the other GDMT classes
  // it mirrors the drug_class (sglt2i / betaBlockers / mras).
  medication_statement_class?: TitrationMedicationCategory;
}

/**
 * The discontinued (patient's current) medication swapped out during titration.
 * `drug_class` is inherited from the base — it's the same class as the replacement.
 */
export type DiscontinuedTitrationMedicationInput = BaseTitrationMedicationInput;

export interface TitrationMedicationInput extends BaseTitrationMedicationInput {
  discontinued_medication?: DiscontinuedTitrationMedicationInput;
}

// ===================================================
// TypeScript Types: createTitrationCarePlan
// ===================================================

export interface CreateTitrationCarePlanInput {
  patient_id: string;
  practitioner_id: string;
  start_date: string;
  program: TitrationProgram;
  medications: TitrationMedicationInput[];
}

// ===================================================
// TypeScript Types: endTitrationCarePlan
// ===================================================

export interface EndTitrationCarePlanInput {
  end_date?: string | null;
  patient_id: string;
  program: TitrationProgram;
  reason_note?: string | null;
  reasons: EndTitrationDecision[];
}

export interface EndTitrationCarePlanData {
  endTitrationCarePlan: string[];
}

// ===================================================
// Enums
// ===================================================

export enum CarePlanStatus {
  draft = "draft",
  active = "active",
  onHold = "on-hold",
  revoked = "revoked",
  completed = "completed",
  enteredInError = "entered-in-error",
  unknown = "unknown",
}

export enum MedicationRequestStatus {
  active = "active",
  onHold = "onHold",
  cancelled = "cancelled",
  completed = "completed",
  enteredInError = "enteredInError",
  stopped = "stopped",
  draft = "draft",
  unknown = "unknown",
}

/* MedicationRequest.category[].coding[].code (system: https://vitall.com/documentation) */
export enum GdmtMedicationClass {
  Rasi = "rasi",
  Sglt2i = "sglt2i",
  Mra = "mras",
  BetaBlocker = "betaBlockers",
}

/** BE GraphQL enum — distinct casing from the FE `GdmtMedicationClass` keys. */
export enum TitrationDrugClass {
  RASi = "RASi",
  SGLT2i = "SGLT2i",
  nsMRA = "nsMRA",
  BetaBlocker = "BetaBlocker",
}

export enum TitrationMedicationCategory {
  acei = "acei",
  arbs = "arbs",
  arnis = "arnis",
  betaBlockers = "betaBlockers",
  mras = "mras",
  sglt2i = "sglt2i",
}

export enum TitrationProgram {
  HeartFailureGdmt = "HeartFailureGdmt",
}

export enum TitrationDecision {
  Increase = "Increase",
  Decrease = "Decrease",
  Continue = "Continue",
  Hold = "Hold",
  Discontinue = "Discontinue",
}

/** BE GraphQL enum — values must match the `EndTitrationDecision` enum members exactly. */
export enum EndTitrationDecision {
  MaxDoseTolerated = "MaxDoseTolerated",
  AbnormalBloodwork = "AbnormalBloodwork",
  DecliningFurther = "DecliningFurther",
  Other = "Other",
}
